/**
 * PortalContext — Central state management for the License Portal.
 *
 * Follows the { state, actions, meta } pattern.
 * All API calls flow through this context so components stay decoupled from fetch logic.
 */

import { createContext, useContext, useReducer, useCallback, useMemo } from 'react';
import * as api from '../lib/api.js';
import { usePortalSession } from '../hooks/usePortalSession.js';

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

const PortalContext = createContext(null);

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

const initialState = {
    license: null, // { id, productId, edition, maintenanceEnd, maxDevices, status }
    devices: [], // [{ device_code, device_name, added_at }]
    isLoading: false,
    error: null, // { error: string, message?: string } | null
};

function reducer(state, action) {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, isLoading: true, error: null };
        case 'CLEAR_LOADING':
            return { ...state, isLoading: false };
        case 'SET_ERROR':
            return { ...state, isLoading: false, error: action.payload };
        case 'CLEAR_ERROR':
            return { ...state, error: null };
        case 'SET_LICENSE_DATA':
            return {
                ...state,
                isLoading: false,
                error: null,
                license: action.payload.license,
                devices: action.payload.devices,
            };
        case 'SET_DEVICES':
            return { ...state, devices: action.payload, isLoading: false, error: null };
        case 'ADD_DEVICE':
            return {
                ...state,
                isLoading: false,
                error: null,
                devices: [...state.devices, action.payload],
            };
        case 'REMOVE_DEVICE':
            return {
                ...state,
                isLoading: false,
                error: null,
                devices: state.devices.filter((d) => d.device_code !== action.payload),
            };
        case 'UPDATE_MAINTENANCE':
            return {
                ...state,
                isLoading: false,
                error: null,
                license: state.license
                    ? { ...state.license, maintenanceEnd: action.payload }
                    : state.license,
            };
        case 'RESET':
            return initialState;
        default:
            return state;
    }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function PortalProvider({ children }) {
    const [state, dispatch] = useReducer(reducer, initialState);
    const { token, licenseId, isAuthenticated, isExpired, saveSession, clearSession } =
        usePortalSession();

    // -- Actions ------------------------------------------------------------

    const lookup = useCallback(
        async (email, licenseKey) => {
            dispatch({ type: 'SET_LOADING' });
            try {
                const data = await api.lookupLicense(email, licenseKey);
                saveSession(data.sessionToken);
                dispatch({
                    type: 'SET_LICENSE_DATA',
                    payload: {
                        license: {
                            id: data.licenseId,
                            productId: data.productId,
                            edition: data.edition,
                            maintenanceEnd: data.maintenanceEnd,
                            maxDevices: data.maxDevices,
                            status: data.status,
                        },
                        devices: data.devices,
                    },
                });
                return data;
            } catch (err) {
                dispatch({
                    type: 'SET_ERROR',
                    payload: { error: err.error || 'unknown', message: err.message },
                });
                throw err;
            }
        },
        [saveSession]
    );

    const addDeviceAction = useCallback(
        async (deviceCode, deviceName) => {
            dispatch({ type: 'SET_LOADING' });
            try {
                await api.addDevice(token, licenseId, deviceCode, deviceName);
                dispatch({
                    type: 'ADD_DEVICE',
                    payload: {
                        device_code: deviceCode,
                        device_name: deviceName || null,
                        added_at: new Date().toISOString(),
                    },
                });
            } catch (err) {
                dispatch({
                    type: 'SET_ERROR',
                    payload: { error: err.error || 'unknown', message: err.message },
                });
                throw err;
            }
        },
        [token, licenseId]
    );

    const removeDeviceAction = useCallback(
        async (deviceCode) => {
            dispatch({ type: 'SET_LOADING' });
            try {
                await api.removeDevice(token, licenseId, deviceCode);
                dispatch({ type: 'REMOVE_DEVICE', payload: deviceCode });
            } catch (err) {
                dispatch({
                    type: 'SET_ERROR',
                    payload: { error: err.error || 'unknown', message: err.message },
                });
                throw err;
            }
        },
        [token, licenseId]
    );

    const issueBundleAction = useCallback(async () => {
        dispatch({ type: 'SET_LOADING' });
        try {
            const data = await api.issueBundle(token, licenseId);
            dispatch({ type: 'CLEAR_LOADING' });
            return data;
        } catch (err) {
            dispatch({
                type: 'SET_ERROR',
                payload: { error: err.error || 'unknown', message: err.message },
            });
            throw err;
        }
    }, [token, licenseId]);

    const renewAction = useCallback(async () => {
        dispatch({ type: 'SET_LOADING' });
        try {
            const data = await api.renewLicense(token, licenseId);
            dispatch({ type: 'UPDATE_MAINTENANCE', payload: data.maintenanceEnd });
            return data;
        } catch (err) {
            dispatch({
                type: 'SET_ERROR',
                payload: { error: err.error || 'unknown', message: err.message },
            });
            throw err;
        }
    }, [token, licenseId]);

    const hydrateFromPurchase = useCallback(
        (data) => {
            if (!data?.sessionToken || !data?.licenseId) return false;

            saveSession(data.sessionToken);
            dispatch({
                type: 'SET_LICENSE_DATA',
                payload: {
                    license: {
                        id: data.licenseId,
                        productId: data.productId,
                        edition: data.edition,
                        maintenanceEnd: data.maintenanceEnd,
                        maxDevices: data.maxDevices,
                        status: data.licenseStatus || data.status,
                    },
                    devices: data.devices || [],
                },
            });
            return true;
        },
        [saveSession]
    );

    const logout = useCallback(() => {
        clearSession();
        dispatch({ type: 'RESET' });
    }, [clearSession]);

    const clearError = useCallback(() => {
        dispatch({ type: 'CLEAR_ERROR' });
    }, []);

    // -- Memoised context value ---------------------------------------------

    const value = useMemo(
        () => ({
            state,
            actions: {
                lookup,
                addDevice: addDeviceAction,
                removeDevice: removeDeviceAction,
                issueBundle: issueBundleAction,
                renew: renewAction,
                hydrateFromPurchase,
                logout,
                clearError,
            },
            meta: {
                isAuthenticated,
                isSessionExpired: isExpired,
                token,
                licenseId,
            },
        }),
        [
            state,
            lookup,
            addDeviceAction,
            removeDeviceAction,
            issueBundleAction,
            renewAction,
            hydrateFromPurchase,
            logout,
            clearError,
            isAuthenticated,
            isExpired,
            token,
            licenseId,
        ]
    );

    return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function usePortal() {
    const ctx = useContext(PortalContext);
    if (!ctx) {
        throw new Error('usePortal must be used within <PortalProvider>');
    }
    return ctx;
}
